import { ActionIcon, Badge, Popover, Tooltip } from '@mantine/core';
import { IconBrowserOff, IconEye, IconSettings } from '@tabler/icons-react';
import { useState } from 'react';
import { useUpsertOperation } from '~/hooks/mutations/operations/useUpsertOperation';
import type { SelectedOperation } from '~/server/logic/prisma/operation-select';
import type {
  AIAssertionModel,
  AIAssertionScreenshotType,
  OperationParentContext,
} from '~/server/logic/steps/operation/types';
import AIAssertionOpConfig from './AIAssertionOpConfig';
import AIOpInPlaceEdit from '../AIOpInPlaceEdit';

export default function AIAssertionOp({
  operation,
  testContentId,
  stepId,
  stepOrder,
  previewCursorAllowed,
  readonly,
  disableMenu,
  parentContext,
}: {
  operation: SelectedOperation;
  testContentId: string;
  stepId: string;
  stepOrder: number;
  previewCursorAllowed: boolean;
  readonly?: boolean;
  disableMenu?: boolean;
  parentContext?: OperationParentContext;
}) {
  const [isGearPopoverOpen, setIsGearPopoverOpen] = useState(false);

  const {
    mutateAsync: upsertOperation,
    isPending: isSaving,
    variables: mutationVariables,
  } = useUpsertOperation({
    testContentId,
    stepId,
    parentContext,
  });

  const handleGearSave = async (values: {
    assertion: string;
    includeScreenshot: boolean;
    screenshotType: AIAssertionScreenshotType;
    model: AIAssertionModel;
  }) => {
    setIsGearPopoverOpen(false);
    await upsertOperation({
      operation: {
        operationId: operation.id,
        pageAlias: operation.pageAlias,
        opensPageAlias: operation.opensPageAlias || undefined,
        parentContext,
        operation: {
          operationType: 'AI_ASSERTION',
          assertion: values.assertion,
          includeScreenshot: values.includeScreenshot,
          screenshotType: values.includeScreenshot ? values.screenshotType : 'VIEWPORT',
          model: values.model,
        },
      },
    }).catch(() => undefined);
  };

  const handlePromptUpdate = async (assertion: string) => {
    await upsertOperation({
      operation: {
        operationId: operation.id,
        pageAlias: operation.pageAlias,
        opensPageAlias: operation.opensPageAlias || undefined,
        parentContext,
        operation: {
          operationType: 'AI_ASSERTION',
          assertion,
          includeScreenshot: operation.aiAssertionOp?.includeScreenshot ?? true,
          screenshotType: operation.aiAssertionOp?.screenshotType || 'VIEWPORT',
          model: operation.aiAssertionOp?.model || 'STABLY_FLASH',
        },
      },
    }).catch(() => undefined);
  };

  const badge = (
    <Badge
      leftSection={<IconEye size={14} />}
      color="grape"
      variant="filled"
      className="bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700 dark:bg-purple-900/20 dark:text-purple-400"
      classNames={{ root: 'normal-case' }}
    >
      Assert
    </Badge>
  );

  const additionalIcons = (
    <>
      {!(operation.aiAssertionOp?.includeScreenshot ?? true) && (
        <Tooltip label="No screenshot">
          <ActionIcon
            variant="transparent"
            radius="xl"
            color="gray"
            size="xs"
            onClick={() => {
              setIsGearPopoverOpen((prev) => !prev);
            }}
          >
            <IconBrowserOff size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </>
  );

  const localPrompt =
    isSaving && mutationVariables?.operation.operation.operationType === 'AI_ASSERTION' ?
      mutationVariables.operation.operation.assertion
    : undefined;
  const remotePrompt = operation.aiAssertionOp?.assertion;

  const gearIconContent = (
    <Popover
      opened={isGearPopoverOpen}
      onClose={() => setIsGearPopoverOpen(false)}
      onDismiss={() => setIsGearPopoverOpen(false)}
      position="bottom"
      width={400}
      withArrow
      shadow="md"
      closeOnClickOutside
    >
      <Popover.Target>
        <ActionIcon
          size="xs"
          radius="xl"
          variant="transparent"
          color="gray"
          className="hover:bg-gray-100 hover:dark:bg-gray-800"
          onClick={() => {
            setIsGearPopoverOpen(!isGearPopoverOpen);
          }}
        >
          <IconSettings />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown className="dark:bg-neutral-800">
        <AIAssertionOpConfig
          readonly={readonly}
          operation={operation}
          onClose={() => setIsGearPopoverOpen(false)}
          onSave={handleGearSave}
          optimisticPrompt={localPrompt}
        />
      </Popover.Dropdown>
    </Popover>
  );

  return (
    <AIOpInPlaceEdit
      operation={operation}
      testContentId={testContentId}
      stepOrder={stepOrder}
      previewCursorAllowed={previewCursorAllowed}
      readonly={readonly}
      disableMenu={disableMenu}
      parentContext={parentContext}
      badge={badge}
      gearIconContent={gearIconContent}
      prompt={(localPrompt ?? remotePrompt ?? '').trim()}
      placeholderText="Click to enter prompt"
      onPromptUpdate={handlePromptUpdate}
      additionalIcons={additionalIcons}
    />
  );
}
